import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
} from "@mui/material";
import { TableFooter } from "@mui/material";
import Swal from "sweetalert2";
// import api from "../api/api";
import api from "../../api/api";
import testlogo from "./messiahnewlogo.jpg";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

const GradesPerformance = () => {
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const reportRef = useRef();

  const token = localStorage.getItem("access");

  useEffect(() => {
    const fetchPeriods = async () => {
      if (!token) return;
      try {
        const res = await api.get("/api/users/periods/", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPeriods(res.data);
      } catch {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch periods.",
        });
      }
    };
    fetchPeriods();
  }, [token]);

  const handlePeriodChange = (e) => setSelectedPeriod(e.target.value);

  const fetchGrades = async (period) => {
    try {
      const res = await api.get("/api/users/student-averages/", {
        headers: { Authorization: `Bearer ${token}` },
        params: { period },
      });
      if (res.data.length === 0) {
        Swal.fire({
          icon: "info",
          title: "No grades",
          text: "We have not published your grades.",
        });
        setGrades([]);
      } else {
        setGrades(res.data);
      }
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch grades.",
      });
      setGrades([]);
    }
  };

  const handleViewGrades = () => {
    if (!selectedPeriod) return;
    setLoading(true);
    setTimeout(async () => {
      await fetchGrades(selectedPeriod);
      setLoading(false);
    }, 3000);
  };

  const downloadPDF = () => {
    const input = reportRef.current;
    if (!input) return;
    html2canvas(input, { scale: 2 }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF();
      const imgWidth = 190;
      const pageHeight = pdf.internal.pageSize.height;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      pdf.save("GradeSheet.pdf");
    });
  };

  return (
    <Box sx={{ p: 3, maxWidth: 700, mx: "auto" }}>
      <Typography variant="h4" gutterBottom textAlign="center">
        Grade Report
      </Typography>

      {/* Period Selection */}
      <FormControl fullWidth margin="normal">
        <InputLabel>Period</InputLabel>
        <Select label="Period" value={selectedPeriod} onChange={handlePeriodChange}>
          {periods.map((period) => (
            <MenuItem key={period.id} value={period.id}>
              {period.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        variant="contained"
        color="primary"
        onClick={handleViewGrades}
        disabled={!selectedPeriod}
        sx={{ mb: 2 }}
      >
        {loading ? "Loading..." : "View Grades"}
      </Button>

      {loading && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mt: 2,
          }}
        >
          <CircularProgress />
          <Typography sx={{ mt: 1, textAlign: "center" }}>
            GradeSheet Loading Please Wait!
          </Typography>
        </Box>
      )}

      {!loading && grades.length > 0 && (
        <Box ref={reportRef} sx={{ mb: 3, fontSize: "0.75rem", width: "100%", maxWidth: 700, mx: "auto", p: 1 }}>
  {/* Header Section */}
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      mb: 1,
      gap: 1,
      p: 1,
      backgroundColor: "#f0f0f0",
      borderRadius: 2,
    }}
  >
    <img
      src={testlogo}
      alt="School Logo"
      style={{ width: 60, borderRadius: 6 }}
    />
    <Box sx={{ textAlign: "left" }}>
      <Typography variant="h6" fontWeight="bold" sx={{ fontSize: "1rem", lineHeight: 1.2 }}>
        Zion Praise Academy - CRM School System
      </Typography>
      <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
        Location: Cowfield 1st Junction Duportroad, Paynesville, Monrovia, Liberia
      </Typography>
      <Typography variant="body2" fontWeight="medium" sx={{ fontSize: "0.8rem" }}>
        Student Periodic Report
      </Typography>
    </Box>
  </Box>

  {/* Student Info */}
  <Box sx={{ px: 1, mb: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
    <Typography><strong>Student:</strong> {grades[0].student_name}</Typography>
    <Typography><strong>Grade Class:</strong> {grades[0].grade_class_name}</Typography>
    <Typography><strong>Period:</strong> {grades[0].period_name}</Typography>
  </Box>

  {/* Grades Table */}
  <TableContainer sx={{ maxWidth: 600, mx: "auto", border: "1px solid #ccc", borderRadius: 1 }}>
  <Table size="small" sx={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
    <TableHead>
      <TableRow sx={{ backgroundColor: "#f5f5f5", borderBottom: "1px solid #ddd" }}>
        <TableCell
          align="center"
          sx={{ fontWeight: "bold", fontSize: "0.8rem", padding: "6px 4px", borderRight: "1px solid #ddd" }}
        >
          Subject
        </TableCell>
        <TableCell
          align="center"
          sx={{ fontWeight: "bold", fontSize: "0.8rem", padding: "6px 4px" }}
        >
          Final Score
        </TableCell>
      </TableRow>
    </TableHead>

    <TableBody>
      {grades[0].student_subjects.map((subject, idx) => (
        <TableRow
          key={idx}
          sx={{
            backgroundColor: idx % 2 === 0 ? "#fafafa" : "white",
            borderBottom: "1px solid #ddd",
          }}
        >
          <TableCell
            align="center"
            sx={{
              fontSize: "0.75rem",
              padding: "4px 6px",
              borderRight: "1px solid #ddd",
            }}
          >
            {subject.subject_name}
          </TableCell>
          <TableCell
            align="center"
            sx={{
              fontSize: "0.75rem",
              padding: "4px 6px",
              color: parseFloat(subject.formatted_final_score) >= 70 ? "blue" : "red",
              fontWeight: parseFloat(subject.formatted_final_score) >= 70 ? "normal" : "bold",
            }}
            dangerouslySetInnerHTML={{ __html: subject.formatted_final_score }}
          />
        </TableRow>
      ))}
    </TableBody>

    <TableFooter>
      <TableRow sx={{ borderTop: "2px solid #bbb", backgroundColor: "#f5f5f5" }}>
        <TableCell
          align="center"
          sx={{ fontWeight: "bold", fontSize: "0.75rem", padding: "6px 4px", borderRight: "1px solid #ddd" }}
        >
          Average
        </TableCell>
        <TableCell
          align="center"
          sx={{
            color: parseFloat(grades[0].average_score) >= 70 ? "blue" : "red",
            fontWeight: parseFloat(grades[0].average_score) >= 70 ? "normal" : "bold",
            fontSize: "0.75rem",
            padding: "6px 4px",
          }}
        >
          {grades[0].average_score}
        </TableCell>
      </TableRow>
      <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
        <TableCell
          align="center"
          sx={{ fontWeight: "bold", fontSize: "0.75rem", padding: "6px 4px", borderRight: "1px solid #ddd" }}
        >
          Rank
        </TableCell>
        <TableCell align="center" sx={{ fontSize: "0.75rem", padding: "6px 4px" }}>
          {grades[0].rank}
        </TableCell>
      </TableRow>
    </TableFooter>
  </Table>
</TableContainer>


  {/* Remarks */}
  <Box sx={{ mt: 1, fontSize: "0.75rem", px: 1 }}>
    <strong>Remark:</strong> {grades[0].remark}
  </Box>

  {/* Download Button */}
  <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
    <Button variant="contained" color="primary" size="small" onClick={downloadPDF}>
      Download PDF
    </Button>
  </Box>
</Box>

      )}

      {error && (
        <Typography color="error" sx={{ mt: 2 }}>
          Error: {error}
        </Typography>
      )}
    </Box>
  );
};

export default GradesPerformance;

